'use client';

import { useState } from 'react';

import Image from 'next/image';

import { Clock, Dumbbell, Info, ShieldAlert, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { publicStorageUrl } from '@kit/shared/storage';
import { formatTimeToAmPm } from '@kit/shared/utils';

import { PlayerAvatarStack } from '~/(tenant)/_components/participants-preview';
import { imageFocalPosition } from '~/lib/utils/image-focal-point';

import type { ClassCardProps } from './class-card';
import styles from './native-class-card.module.css';

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  kids_academy: 'categoryKidsAcademy',
  group_classes: 'categoryGroupClasses',
  semi_private: 'categorySemiPrivate',
  event: 'categoryEvent',
  tournament: 'categoryTournament',
};

export function NativeClassCardSummary({
  classData,
  displayName,
  imageUrl,
  onImageError,
  isSelected,
  isDisabled,
  onSelect,
  onDetails,
  statusLabel,
  status,
  eligibilityMessage,
  eligibilityTone = 'neutral',
  players,
}: {
  classData: ClassCardProps['classData'];
  displayName: string;
  imageUrl: string | null;
  onImageError: () => void;
  isSelected: boolean;
  isDisabled: boolean;
  onSelect: () => void;
  onDetails: () => void;
  statusLabel?: string;
  status: 'booked' | 'full' | 'limited' | 'available';
  eligibilityMessage?: string;
  /** Ineligible is an error; "log in to check" stays neutral. */
  eligibilityTone?: 'danger' | 'neutral';
  players: { name: string; pictureUrl: string | null }[];
}) {
  const { t, i18n } = useTranslation('bookings');
  const { t: tClasses } = useTranslation('classes');
  const [avatarError, setAvatarError] = useState(false);
  const canJoinWaitlist =
    status === 'full' && classData.waitlist_enabled && !isDisabled;
  const localizedCategory = i18n.language.startsWith('ar')
    ? classData.class_category_name_alt || classData.class_category_name
    : classData.class_category_name || classData.class_category_name_alt;
  const categoryLabelKey = CATEGORY_LABEL_KEYS[localizedCategory ?? ''];
  const categoryFallback = localizedCategory?.replace(/_+/g, ' ').trim();
  const category = ['semi_private', 'group_classes'].includes(
    classData.class_category_name ?? '',
  )
    ? null
    : categoryLabelKey
      ? tClasses(categoryLabelKey, { defaultValue: categoryFallback })
      : categoryFallback;
  const toMinutes = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    return (hours ?? 0) * 60 + (minutes ?? 0);
  };
  const duration =
    (toMinutes(classData.end_time) - toMinutes(classData.start_time) + 1440) %
    1440;
  const timeLabel = `${formatTimeToAmPm(classData.start_time, i18n.language)} – ${formatTimeToAmPm(classData.end_time, i18n.language)}`;

  return (
    <div className={styles.summary} data-test="native-class-card">
      <button
        type="button"
        className={styles.select}
        aria-label={`${displayName}, ${timeLabel}${statusLabel ? `, ${statusLabel}` : ''}`}
        aria-pressed={isSelected}
        disabled={isDisabled}
        onClick={onSelect}
        data-test="select-class"
      />
      <div className={styles.image} aria-hidden="true">
        {imageUrl ? (
          <Image
            src={publicStorageUrl(imageUrl)}
            alt=""
            fill
            sizes="(max-width: 640px) 28vw, 180px"
            className="object-cover"
            style={{
              objectPosition: imageFocalPosition(
                classData.image_focal_x,
                classData.image_focal_y,
              ),
            }}
            onError={onImageError}
          />
        ) : (
          <Dumbbell size={28} strokeWidth={1.25} />
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.heading}>
          {category && <span className={styles.category}>{category}</span>}
        </div>
        <h3 className={styles.title}>{displayName}</h3>
        <div className={styles.time}>
          <span dir="auto">{timeLabel}</span>
          {duration > 0 && (
            <span className={styles.duration}>
              <Clock size={12} aria-hidden="true" />
              {t('minDuration', { count: duration })}
            </span>
          )}
        </div>
        <div className={styles.footer}>
          {classData.trainer_name && (
            <span className={styles.trainer}>
              <span className={styles.avatar}>
                {classData.trainer_avatar_url && !avatarError ? (
                  <Image
                    src={publicStorageUrl(classData.trainer_avatar_url)}
                    alt=""
                    fill
                    sizes="24px"
                    className="object-cover"
                    onError={() => setAvatarError(true)}
                  />
                ) : (
                  <User size={14} aria-hidden="true" />
                )}
              </span>
              <span>{classData.trainer_name}</span>
            </span>
          )}
          {canJoinWaitlist ? (
            <button
              type="button"
              className={styles.waitlist}
              style={{
                minHeight: 0,
                padding: 0,
                border: 0,
                borderRadius: 0,
                background: 'transparent',
                color: 'inherit',
                font: 'inherit',
                boxShadow: 'none',
              }}
              onClick={onSelect}
              aria-label={t('joinWaitlist')}
              data-test="join-class-waitlist"
            >
              <span className={styles.status} data-status="full">
                {t('common:nativeHome.waitlist')}
              </span>
            </button>
          ) : (
            statusLabel && (
              <span className={styles.status} data-status={status}>
                {statusLabel}
              </span>
            )
          )}
        </div>
        {eligibilityMessage && (
          <p className={styles.eligibility} data-tone={eligibilityTone}>
            <ShieldAlert size={14} aria-hidden="true" />
            {eligibilityMessage}
          </p>
        )}
        {players.length > 0 && (
          <div className={styles.players}>
            <PlayerAvatarStack players={players} max={4} />
          </div>
        )}
      </div>
      <button
        type="button"
        className={styles.details}
        onClick={onDetails}
        aria-label={`${t('viewDetails', { defaultValue: 'View details' })}: ${displayName}`}
        data-test="class-details"
      >
        <Info size={18} aria-hidden="true" />
      </button>
    </div>
  );
}
